import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import CONFIG from "config/config";
import { useStore } from "store";
import { StoreType } from "store/types";
import { toFormatChannel } from "./channels";
import { FormatsStatus, getFormatsState, loadRuntimeFormats, subscribeRuntimeFormats } from "./formatsStore";
import { FormatChannel, RuntimeFormatConfig } from "./types";

export const FORMATS_FROM_API_FLAG = "formats_from_api";

// PostHog never answering (no key, blocked) must not hold the UI forever.
const FLAG_RESOLUTION_TIMEOUT_MS = 8000;

interface FormatsFlag {
  enabled: boolean;
  // True while PostHog has not answered yet (and no local override applies).
  pending: boolean;
}

export const useFormatsFromApiFlag = (): FormatsFlag => {
  const remote = useFeatureFlagEnabled(FORMATS_FROM_API_FLAG);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (remote !== undefined || CONFIG.FORMATS_FROM_API) return;
    const handle = setTimeout(() => setTimedOut(true), FLAG_RESOLUTION_TIMEOUT_MS);
    return () => clearTimeout(handle);
  }, [remote]);

  return {
    enabled: CONFIG.FORMATS_FROM_API || remote === true,
    pending: !CONFIG.FORMATS_FROM_API && remote === undefined && !timedOut,
  };
};

interface UseFormatsOptions {
  countryId?: string | number | null;
  channel?: FormatChannel | null;
  // Re-request on first load, bypassing the cache (pre-signed template URLs expire).
  refreshOnMount?: boolean;
}

export interface UseFormatsResult {
  formats: RuntimeFormatConfig[] | null;
  // True only when the flag is on and the API answered with formats; false means "use the hardcoded list".
  isActive: boolean;
  // Flag unresolved or request in flight: callers that need a catalogue-only code should wait.
  isPending: boolean;
  status: FormatsStatus;
}

export function useFormats({ countryId, channel, refreshOnMount = false }: UseFormatsOptions): UseFormatsResult {
  const flag = useFormatsFromApiFlag();
  const { formats, status } = useSyncExternalStore(subscribeRuntimeFormats, getFormatsState);
  const canLoad = flag.enabled && !!countryId && !!channel;

  const refreshPending = useRef(refreshOnMount);

  useEffect(() => {
    loadRuntimeFormats({
      enabled: canLoad,
      countryId: countryId as string,
      channel: channel as FormatChannel,
      fresh: canLoad && refreshPending.current,
    });
    if (canLoad) refreshPending.current = false;
  }, [canLoad, countryId, channel]);

  return {
    formats,
    isActive: formats !== null,
    isPending: flag.pending || (canLoad && status !== "ready" && status !== "fallback"),
    status,
  };
}

// Loads the catalogue for the campaign in the store; mount once on each page that reads formats.
export function useCampaignFormats({ refreshOnMount = false }: { refreshOnMount?: boolean } = {}): UseFormatsResult {
  const {
    campaign: { channel, subtype },
    country,
  }: StoreType = useStore();
  return useFormats({ countryId: country?.id, channel: toFormatChannel(channel, subtype), refreshOnMount });
}

// Read-only subscription for components that only consume the loaded catalogue (loading is mounted at page level).
export function useRuntimeFormats(): RuntimeFormatConfig[] | null {
  return useSyncExternalStore(subscribeRuntimeFormats, getFormatsState).formats;
}
